/* (c) Quick Pro USA (quickprousa.com) - proprietary theme code. All rights reserved. */
/* <qp-pdp-stage> — media stage behaviour: colour swap of attached media,
   counter, autoplay guards, pause/sound, thumbs. */
(function () {
  'use strict';
  if (typeof customElements === 'undefined' || customElements.get('qp-pdp-stage')) return;
  var EVT = 'qp-qs:state';

  /* one-shot class that removes itself (animationend, or after ms when
     reduced motion runs no animation) */
  function oneShot(el, cls, ms) {
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
    var done = function () {
      el.classList.remove(cls);
      el.removeEventListener('animationend', done);
      clearTimeout(t);
    };
    var t = setTimeout(done, ms);
    el.addEventListener('animationend', done);
  }
  function reduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  class QpPdpStage extends HTMLElement {
    connectedCallback() {
      var self = this;
      this._track = this.querySelector('[data-stage-track]');
      if (!this._track) return;
      if (this._ac) this._ac.abort();
      var ac = this._ac = new AbortController();
      var sig = { signal: ac.signal };
      document.addEventListener(EVT, function (e) { self._applyColor(e && e.detail && e.detail.color, true); }, sig);
      var later = function () {
        clearTimeout(self._tick);
        self._tick = setTimeout(function () { self._updateCounter(); }, 120);
      };
      var now = function () { clearTimeout(self._tick); self._updateCounter(); };
      this._track.addEventListener('scrollend', now, sig);
      this._track.addEventListener('scroll', later, { passive: true, signal: ac.signal });
      /* mobile swipe hint teaches once: it goes after the first real swipe
         (nothing persisted) */
      this._track.addEventListener('scroll', function () {
        if ((self._track.scrollLeft || 0) > 4) self.classList.add('is-swiped');
      }, { passive: true, signal: ac.signal });
      /* colour swatches: warm the next lead image before the click lands */
      var warm = function (e) {
        var sw = e.target && e.target.closest && e.target.closest('[data-variant-img][data-swatch-color]');
        if (sw) self._preload(sw);
      };
      document.addEventListener('mouseover', warm, sig);
      document.addEventListener('focusin', warm, sig);
      window.addEventListener('scroll', function () { if (self._gridOn()) later(); }, { passive: true, signal: ac.signal });
      this.addEventListener('click', function (e) {
        var t = e.target.closest && e.target.closest('[data-stage-pause],[data-stage-sound],[data-media-target]');
        if (!t || !self.contains(t)) return;
        if (t.hasAttribute('data-stage-pause')) self._togglePlay();
        else if (t.hasAttribute('data-stage-sound')) self._toggleSound();
        else self._goTo(t.getAttribute('data-media-target'));
      }, sig);
      var more = this.querySelector('[data-stage-more]');
      if (more) {
        more.addEventListener('click', function () {
          self._expanded = true;
          self._syncGrid();
          self._updateCounter();
        }, sig);
      }
      window.addEventListener('resize', function () { self._syncGrid(); self._updateCounter(); }, { passive: true, signal: ac.signal });
      this._calm();
      this._film();
      this._lazyFade();
      var st = window.__qpQsState;
      if (st && st.color) this._applyColor(st.color, true);
      this._syncGrid();
      this._updateCounter();
    }
    disconnectedCallback() {
      if (this._ac) this._ac.abort();
      this._ac = null;
      clearTimeout(this._tick);
    }
    _isStack() { return this.getAttribute('data-layout') === 'stack'; }
    /* Desktop bundle gallery (PM 2026-10-22): the first visible item leads
       full width, the rest are square tiles, and only the first four tiles
       show until SHOW ALL is pressed. Mobile (1-up swipe track) and the hero
       layout are never touched: no classes, no fold. */
    _gridOn() {
      return this._isStack() && !!(window.matchMedia && window.matchMedia('(min-width: 1024px)').matches);
    }
    _syncGrid() {
      var items = this._items();
      var more = this.querySelector('[data-stage-more]');
      var FOLD = 4;
      items.forEach(function (el) { el.classList.remove('qp-stage__item--lead'); });
      if (!this._gridOn()) {
        items.forEach(function (el) { el.classList.remove('is-folded'); });
        if (more) more.hidden = true;
        return;
      }
      var vis = items.filter(function (el) { return !el.hasAttribute('hidden'); });
      if (vis[0]) vis[0].classList.add('qp-stage__item--lead');
      var tiles = vis.slice(1);
      var folded = !this._expanded && tiles.length > FOLD;
      items.forEach(function (el) {
        var fold = folded && tiles.indexOf(el) >= FOLD;
        if (!fold && el.classList.contains('is-folded') && !el.hasAttribute('hidden')) oneShot(el, 'is-unfold-in', 400);
        el.classList.toggle('is-folded', fold);
      });
      if (more) {
        more.hidden = !folded;
        more.setAttribute('aria-expanded', folded ? 'false' : 'true');
        var tpl = more.getAttribute('data-more-template') || 'SHOW ALL {n} PHOTOS';
        var moreLbl = more.querySelector('[data-stage-more-label]') || more;
        moreLbl.textContent = tpl.replace('{n}', String(tiles.length));
      }
    }
    /* Film first frame: the poster is the film's own first frame (an <img>
       under the video, the LCP). The video sits over it at opacity 0 and
       crossfades in on its first `playing`; controls + counter follow. All
       of it is CSS keyed off these classes; reduced motion / data-saver get
       the plain video with its poster attribute. */
    _film() {
      var v = this._video();
      var item = v && v.closest ? v.closest('[data-stage-item]') : null;
      if (!v || !item) return;
      var poster = item.getAttribute('data-media-poster');
      if (poster && !v.getAttribute('poster')) v.setAttribute('poster', poster);
      if (!item.hasAttribute('data-stage-autoplay')) return;
      var sd = navigator.connection && navigator.connection.saveData;
      if (reduced() || sd) return;
      var self = this, timer;
      this.classList.add('has-film-fade');
      var live = function () {
        clearTimeout(timer);
        item.classList.add('is-playing');
        self.classList.add('is-film-live');
      };
      v.addEventListener('playing', live, { once: true });
      if (v.paused === false && v.readyState >= 3) live();
      /* autoplay blocked: the controls must still appear (PLAY) */
      timer = setTimeout(function () { self.classList.add('is-film-live'); }, 2500);
    }
    /* desktop stack: lazy renders below item 1 fade in when they load
       (class set only here, so a script failure leaves them visible) */
    _lazyFade() {
      if (!this._gridOn() || reduced()) return;
      var imgs = this._items().slice(1).map(function (it) { return it.querySelector('img'); }).filter(Boolean);
      imgs.forEach(function (img) {
        if (img.complete && img.naturalWidth) return;
        img.classList.add('is-pending');
        var clear = function () { img.classList.remove('is-pending'); };
        img.addEventListener('load', clear, { once: true });
        img.addEventListener('error', clear, { once: true });
        setTimeout(clear, 4000);
      });
    }
    /* preload the lead render of the colour this swatch stands for */
    _preload(sw) {
      var color = sw.getAttribute('data-value') || sw.getAttribute('value') || sw.getAttribute('data-swatch-color');
      var item = this._items().filter(function (el) {
        var c = el.getAttribute('data-media-color');
        return !!c && !!color && c.split('|').indexOf(color) !== -1;
      })[0];
      var img = item && item.querySelector('img');
      var href = (img && (img.currentSrc || img.getAttribute('src'))) || sw.getAttribute('data-variant-img');
      if (!href) return;
      if (img && img.loading === 'lazy') img.loading = 'eager';
      this._warmed = this._warmed || {};
      if (this._warmed[href]) return;
      this._warmed[href] = true;
      var link = document.createElement('link');
      link.setAttribute('rel', 'preload');
      link.setAttribute('as', 'image');
      link.setAttribute('href', href);
      document.head.appendChild(link);
    }
    _currentIndex() {
      var vis = this._visible();
      if (!vis.length) return 0;
      var i = 0;
      /* the stack layout only exists at >= 1024 (CSS); below that a "stack"
         page is the same 1-up swipe track as everything else, so the
         counter must follow scrollLeft there (PM bug 2026-10-23: the
         mobile counter stuck at 1 / n while swiping) */
      if (this._gridOn()) {
        var mid = (window.innerHeight || 0) / 2;
        var best = Infinity;
        vis.forEach(function (el, n) {
          var r = el.getBoundingClientRect();
          var d = Math.abs((r.top + r.bottom) / 2 - mid);
          if (d < best) { best = d; i = n; }
        });
        return i;
      }
      var w = this._track.clientWidth || 0;
      i = w ? Math.round(this._track.scrollLeft / w) : 0;
      return Math.max(0, Math.min(vis.length - 1, i));
    }
    _items() { return Array.prototype.slice.call(this.querySelectorAll('[data-stage-item]')); }
    _visible() { return this._items().filter(function (el) { return !el.hasAttribute('hidden') && !el.classList.contains('is-folded'); }); }
    _video() { return this.querySelector('video'); }
    _calm() {
      var rm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var sd = navigator.connection && navigator.connection.saveData;
      if (!rm && !sd) return;
      var v = this._video();
      if (v) {
        v.removeAttribute('autoplay');
        v.autoplay = false;
        if (typeof v.pause === 'function') { try { v.pause(); } catch (e) { /* not implemented */ } }
      }
      var p = this.querySelector('[data-stage-pause]');
      if (p) p.textContent = 'PLAY';
    }
    _applyColor(color, lead) {
      if (!color) return;
      var els = this._items().concat(Array.prototype.slice.call(this.querySelectorAll('[data-media-target]')));
      els.forEach(function (el) {
        var c = el.getAttribute('data-media-color');
        if (!c) return;
        if (c.split('|').indexOf(color) !== -1) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
      });
      if (lead) this._lead(color);
      this._syncGrid();
      this._updateCounter();
      this._kitFrame(color);
    }
    /* Bundle kit strip (snippets/qp-build-kit-strip.liquid, outside this
       element): its FRAME tile follows the colour the stage follows. The image
       per colour comes from the recipe blob (frame.variants[].media_src, the
       frame variant's own image); a colour the frame has no image for (a
       twin-only colourway) falls back to the tile's default image. */
    _kitFrame(color) {
      var tile = document.querySelector('[data-kit-tile="frame"]');
      if (!tile) return;
      if (!this._kitMap) {
        var map = {};
        try {
          var el = document.querySelector('script[data-build-data]');
          var f = el ? (JSON.parse(el.textContent) || {}).frame : null;
          if (f && f.variants && typeof f.colorIndex === 'number' && f.colorIndex >= 0) {
            f.variants.forEach(function (v) {
              var c = v.options && v.options[f.colorIndex];
              if (c != null && v.media_src && !map[c]) map[c] = v.media_src;
            });
          }
        } catch (e) { /* no blob: the default image stays */ }
        this._kitMap = map;
      }
      var src = this._kitMap[color]
        ? this._kitMap[color].replace(/([?&]width=)\d+/, '$1480')
        : tile.getAttribute('data-kit-default-src');
      if (!src) return;
      var img = tile.querySelector('img');
      if (!img) {
        var well = tile.querySelector('.qp-kit__well');
        if (!well) return;
        img = document.createElement('img');
        img.className = 'qp-kit__img';
        img.setAttribute('alt', '');
        img.setAttribute('width', '480');
        img.setAttribute('height', '480');
        well.appendChild(img);
      }
      if (img.getAttribute('src') !== src) {
        img.removeAttribute('srcset');
        img.setAttribute('src', src);
      }
    }
    /* A chosen colour LEADS the stage (PM 2026-10-21): its first attached render
       moves to the front of the track and the film (and everything shared)
       follows, on every layout. The swipe track snaps back to the start; the
       desktop stack scrolls the render into view only if it is off-screen.
       The video element itself is never moved, so playback is untouched. */
    _lead(color) {
      var track = this._track;
      if (!track) return;
      var pick = this._items().filter(function (el) {
        var c = el.getAttribute('data-media-color');
        return !!c && c.split('|').indexOf(color) !== -1;
      })[0];
      if (!pick) return;
      if (track.firstElementChild !== pick) track.insertBefore(pick, track.firstElementChild);
      /* only the INCOMING lead item fades (320ms, no outgoing fade, the well
         stays put) — and only when the lead actually changed */
      var id = pick.getAttribute('data-media-id');
      var changed = this._leadId != null && this._leadId !== id;
      if (changed) oneShot(pick, 'is-lead-in', 380);
      this._leadId = id;
      /* the box ratio follows the LEADING item (spec R4: 4:5 only for portrait) */
      var box = this.querySelector('.qp-stage__box');
      if (box) {
        var portrait = pick.hasAttribute('data-portrait');
        box.classList.toggle('qp-stage--ratio-4x5', portrait);
        box.classList.toggle('qp-stage--ratio-4x3', !portrait);
      }
      /* The state event fires on EVERY render (size, spec, route, picker…), not
         just a colour change. Only a NEW lead may move anything: the swipe
         track snaps back and the desktop stack scrolls the render into view
         when the colour changed, never for an unrelated pick (PM bug
         2026-10-25: every selection made after scrolling past the lead image
         yanked the page back up to it). */
      if (!changed) return;
      var desktopStack = this._isStack() && window.matchMedia && window.matchMedia('(min-width: 1024px)').matches;
      if (!desktopStack) {
        /* horizontal 1-up track (mobile, and the hero well): snap back to the lead */
        track.scrollLeft = 0;
      } else if (typeof pick.getBoundingClientRect === 'function') {
        var r = pick.getBoundingClientRect();
        var vh = window.innerHeight || 0;
        if ((r.bottom < 0 || r.top > vh) && typeof pick.scrollIntoView === 'function') {
          pick.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }
    _updateCounter() {
      var vis = this._visible();
      var i = this._currentIndex();
      var c = this.querySelector('[data-stage-counter]');
      if (c) {
        var txt = (vis.length ? i + 1 : 0) + ' / ' + vis.length;
        if (c.textContent !== txt) {
          c.textContent = txt;
          if (this._counted) oneShot(c, 'is-settling', 320); /* value settle; never on first paint */
        }
        this._counted = true;
      }
      var l = this.querySelector('[data-stage-label]');
      if (l && vis[i]) l.textContent = vis[i].getAttribute('data-media-label') || '';
      var id = vis[i] && vis[i].getAttribute('data-media-id');
      this.querySelectorAll('[data-media-target]').forEach(function (b) {
        b.classList.toggle('is-active', b.getAttribute('data-media-target') === id);
      });
    }
    _togglePlay() {
      var v = this._video();
      var p = this.querySelector('[data-stage-pause]');
      if (!v) return;
      if (v.paused) {
        var r = v.play && v.play();
        if (r && r.catch) r.catch(function () {});
        if (p) p.textContent = 'PAUSE';
      } else {
        v.pause();
        if (p) p.textContent = 'PLAY';
      }
    }
    _toggleSound() {
      var v = this._video();
      var s = this.querySelector('[data-stage-sound]');
      if (!v) return;
      v.muted = !v.muted;
      if (s) s.textContent = v.muted ? 'SOUND' : 'MUTE';
    }
    _goTo(id) {
      var el = this._items().filter(function (x) { return x.getAttribute('data-media-id') === id; })[0];
      if (!el) return;
      if (typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
      this._updateCounter();
    }
  }
  customElements.define('qp-pdp-stage', QpPdpStage);
})();
